/**
 * SightingTimelineWidget - Heatmap (7-col × 24-row) showing when sightings occur.
 *
 * Lead insight: human-readable "Typically spotted [day] [period]" using peakDow/peakHour.
 * Grid: filled cells (GitHub-style heatmap) — color intensity = sighting density.
 * Click a cell to open a detail panel below: day, time, count, share of window total,
 * activity label, and 90-day context.
 *
 * Falls back to null when hourlyBuckets is empty (< minSightingsForPattern).
 * @atomic @idempotent
 */
import { memo, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline'

const CELL_H   = 8
const LABEL_HOURS = [0, 6, 12, 18]
const LABEL_TEXTS = ['12a', '6a', '12p', '6p']
const Y_LABEL_W   = 20
const FULL_DAYS   = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function formatHour(h) {
  if (h === 0)  return '12am'
  if (h === 12) return '12pm'
  return h < 12 ? `${h}am` : `${h - 12}pm`
}

function hourToPeriod(h) {
  if (h >= 5  && h < 12) return 'mornings'
  if (h >= 12 && h < 17) return 'afternoons'
  if (h >= 17 && h < 21) return 'evenings'
  return 'late night'
}

function activityLabel(ratio) {
  if (ratio >= 0.8) return { text: 'Peak time',            color: 'text-emerald-600' }
  if (ratio >= 0.5) return { text: 'Moderately active',    color: 'text-amber-500'   }
  if (ratio >= 0.25) return { text: 'Somewhat active',     color: 'text-gray-500'    }
  return                    { text: 'Occasionally spotted', color: 'text-gray-400'    }
}

const SightingTimelineWidget = memo(function SightingTimelineWidget({
  hourlyBuckets,
  peakDow,
  peakHour,
}) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState(null) // { dow, hour, count, ratio }

  const days = useMemo(() =>
    (t('entities.entity.patterns.days', { returnObjects: true }) || ['S','M','T','W','T','F','S'])
      .map(d => (d && d[0]) || d),
    [t]
  )

  const { bucketMap, maxCount, totalInWindow } = useMemo(() => {
    const map = new Map()
    let max = 0, total = 0
    for (const b of (hourlyBuckets || [])) {
      map.set(`${b.dow}-${b.hour}`, b.count)
      if (b.count > max) max = b.count
      total += b.count
    }
    return { bucketMap: map, maxCount: max, totalInWindow: total }
  }, [hourlyBuckets])

  const todayDOW = new Date().getDay()

  const handleCellClick = useCallback((dow, hour, count, ratio) => {
    setSelected(prev =>
      prev?.dow === dow && prev?.hour === hour ? null : { dow, hour, count, ratio }
    )
  }, [])

  if (!hourlyBuckets || hourlyBuckets.length === 0) return null

  const peakInsight = peakDow != null && peakHour != null
    ? `Typically spotted ${FULL_DAYS[peakDow]} ${hourToPeriod(peakHour)}`
    : null

  const selActivity = selected ? activityLabel(selected.ratio) : null
  const selShare    = selected && totalInWindow > 0
    ? Math.round((selected.count / totalInWindow) * 100)
    : 0
  // e.g. "1 in 5" — round to nearest whole number, minimum 1
  const selOneIn    = selected && selected.count > 0
    ? Math.max(1, Math.round(totalInWindow / selected.count))
    : null

  return (
    <div className="mt-2">
      {/* Header row */}
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs text-gray-400">
          {t('entities.entity.patterns.title', 'When they appear')}
        </p>
        {peakInsight && (
          <span className="flex items-center gap-0.5 text-[9px] text-primary font-medium bg-primary/10 rounded-full px-1.5 py-0.5 leading-none">
            <ClockIcon className="w-2.5 h-2.5 flex-shrink-0" />
            {peakInsight}
          </span>
        )}
      </div>

      {/* Heatmap grid */}
      <div className="flex items-start gap-0.5">
        {/* Y-axis labels */}
        <div className="relative flex-shrink-0" style={{ width: Y_LABEL_W, height: 24 * CELL_H }}>
          {LABEL_HOURS.map((h, i) => (
            <span
              key={h}
              className="absolute right-1 text-[8px] leading-none text-gray-300 select-none"
              style={{ top: h * CELL_H - 4 }}
            >
              {LABEL_TEXTS[i]}
            </span>
          ))}
        </div>

        <div className="flex-1 flex gap-px relative">
          {/* Guide lines */}
          {LABEL_HOURS.map(h => (
            <div
              key={h}
              className="absolute left-0 right-0 border-t border-gray-100 pointer-events-none"
              style={{ top: h * CELL_H }}
            />
          ))}

          {Array.from({ length: 7 }, (_, dow) => {
            const isToday = dow === todayDOW
            return (
              <div key={dow} className="flex-1 flex flex-col">
                {Array.from({ length: 24 }, (_, hour) => {
                  const count = bucketMap.get(`${dow}-${hour}`) || 0
                  const ratio = maxCount > 0 ? count / maxCount : 0
                  const isSelected = selected?.dow === dow && selected?.hour === hour
                  const hasDot = count > 0

                  return (
                    <div
                      key={hour}
                      className={[
                        'w-full',
                        hasDot ? 'cursor-pointer' : '',
                      ].join(' ')}
                      style={{ height: CELL_H, padding: '1px 1px' }}
                      onClick={hasDot ? () => handleCellClick(dow, hour, count, ratio) : undefined}
                    >
                      {hasDot && (
                        <div
                          className={[
                            'w-full h-full rounded-[1px] bg-primary transition-all',
                            isSelected ? 'ring-1 ring-primary ring-offset-[1px]' : '',
                          ].join(' ')}
                          style={{ opacity: isSelected ? 1 : 0.12 + ratio * 0.88 }}
                        />
                      )}
                    </div>
                  )
                })}

                <span
                  className={[
                    'text-[9px] leading-none mt-0.5 text-center select-none',
                    isToday ? 'text-primary font-semibold' : 'text-gray-400',
                  ].join(' ')}
                >
                  {days[dow]}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Footer: data context */}
      <p className="text-[9px] text-gray-300 mt-1 text-right leading-none">
        {totalInWindow} sighting{totalInWindow !== 1 ? 's' : ''} · past 90 days
      </p>

      {/* Detail panel — appears when a cell is selected */}
      {selected && (
        <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[12px] font-semibold text-gray-800 leading-tight">
                {FULL_DAYS[selected.dow]}
              </p>
              <p className="text-[11px] text-gray-500 leading-tight mt-0.5">
                {formatHour(selected.hour)}–{formatHour((selected.hour + 1) % 24)}
              </p>
            </div>
            <button
              onClick={() => setSelected(null)}
              className="flex-shrink-0 text-gray-300 hover:text-gray-500 transition-colors mt-0.5"
              aria-label="Dismiss"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-x-3 gap-y-1.5">
            <div>
              <p className="text-[9px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Sightings</p>
              <p className="text-[13px] font-bold text-gray-800 leading-none">{selected.count}</p>
            </div>
            <div>
              <p className="text-[9px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">How common</p>
              <p className="text-[13px] font-bold text-gray-800 leading-none">
                {selOneIn === 1 ? 'Every time' : `1 in ${selOneIn}`}
              </p>
              <p className="text-[9px] text-gray-400 leading-none mt-0.5">
                sightings happen here
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-[9px] text-gray-400 uppercase tracking-wide leading-none mb-0.5">Activity</p>
              <p className={`text-[11px] font-semibold leading-none ${selActivity.color}`}>
                {selActivity.text}
              </p>
            </div>
          </div>

          <p className="text-[9px] text-gray-300 mt-2 leading-none">
            Based on {totalInWindow} sighting{totalInWindow !== 1 ? 's' : ''} over the past 90 days
          </p>
        </div>
      )}
    </div>
  )
})

export default SightingTimelineWidget

